import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, Loader2, ArrowLeft, ChefHat, Mail } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status values: 'idle' | 'loading' | 'success' | 'already' | 'invalid' | 'missing' | 'error'
export default function UnsubscribePage() {
  const { t, lang, toggleLang } = useLanguage();
  const copy = t.unsubscribe;
  const [params] = useSearchParams();
  const email = params.get('e') || '';
  const token = params.get('t') || '';
  const [status, setStatus] = useState('idle');
  // Guard against React.StrictMode double-invocation in dev. Without this,
  // StrictMode cleanup sets cancelled=true on the first run before the API
  // response arrives, leaving the UI stuck on "loading".
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const run = async () => {
      if (!email || !token) {
        setStatus('missing');
        return;
      }
      setStatus('loading');
      try {
        const { data } = await axios.get(`${API}/waitlist/unsubscribe`, {
          params: { email, token },
        });
        if (data?.status === 'unsubscribed') setStatus('success');
        else if (data?.status === 'not_found') setStatus('already');
        else setStatus('error');
      } catch (err) {
        const httpStatus = err?.response?.status;
        setStatus(httpStatus === 401 ? 'invalid' : 'error');
      }
    };
    run();
  }, [email, token]);

  const isPositive = status === 'success' || status === 'already';

  const blockFor = () => {
    if (status === 'loading' || status === 'idle') {
      return {
        Icon: Loader2,
        iconClass: 'text-orange-500 animate-spin',
        title: copy.working,
        body: '',
        tone: 'neutral',
      };
    }
    if (status === 'success') {
      return {
        Icon: CheckCircle2,
        iconClass: 'text-emerald-500',
        title: copy.successTitle,
        body: copy.successBody,
        tone: 'positive',
      };
    }
    if (status === 'already') {
      return {
        Icon: CheckCircle2,
        iconClass: 'text-stone-500',
        title: copy.alreadyTitle,
        body: copy.alreadyBody,
        tone: 'positive',
      };
    }
    if (status === 'missing') {
      return {
        Icon: AlertTriangle,
        iconClass: 'text-amber-500',
        title: copy.missingTitle,
        body: copy.missingBody,
        tone: 'warning',
      };
    }
    return {
      Icon: AlertTriangle,
      iconClass: 'text-rose-500',
      title: copy.errorTitle,
      body: copy.errorBody,
      tone: 'warning',
    };
  };

  const view = blockFor();
  const Icon = view.Icon;

  return (
    <div className="min-h-screen bg-[#FFFDF8] text-stone-900 flex flex-col">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#FFFDF8]/80 border-b border-stone-200/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="unsubscribe-back-home">
            <span className="h-9 w-9 rounded-2xl bg-orange-500 flex items-center justify-center shadow-[0_8px_20px_-8px_rgba(249,115,22,0.6)] transition-transform group-hover:rotate-[-6deg]">
              <ChefHat className="h-5 w-5 text-white" strokeWidth={2.4} />
            </span>
            <span className="font-display font-extrabold text-lg tracking-tight">MealMatcher</span>
          </Link>
          <button
            onClick={toggleLang}
            aria-label="Toggle language"
            data-testid="unsubscribe-lang-toggle"
            className="flex items-center gap-0 rounded-full border border-stone-200 bg-white p-1 text-xs font-bold"
          >
            <span className={`px-2.5 py-1 rounded-full transition-all ${lang === 'no' ? 'bg-stone-900 text-white' : 'text-stone-500'}`}>NO</span>
            <span className={`px-2.5 py-1 rounded-full transition-all ${lang === 'en' ? 'bg-stone-900 text-white' : 'text-stone-500'}`}>EN</span>
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md text-center" data-testid={`unsubscribe-state-${status}`}>
          <div className="mx-auto mb-6 h-20 w-20 rounded-3xl bg-white border border-stone-200 flex items-center justify-center shadow-sm">
            <Icon className={`h-9 w-9 ${view.iconClass}`} strokeWidth={2} />
          </div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-balance">
            {view.title}
          </h1>
          {view.body && <p className="mt-4 text-stone-600 leading-relaxed">{view.body}</p>}

          {email && (status === 'success' || status === 'already') && (
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-100 text-stone-700 text-xs font-semibold">
              <Mail className="h-3.5 w-3.5" /> {email}
            </div>
          )}

          <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-stone-800 active:scale-[0.98] transition-all"
            >
              <ArrowLeft className="h-4 w-4" /> {copy.backHome}
            </Link>
            {isPositive && (
              <Link
                to="/#cta"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white border border-stone-200 text-stone-900 text-sm font-bold hover:border-stone-900 active:scale-[0.98] transition-all"
              >
                {copy.rejoin}
              </Link>
            )}
          </div>
        </div>
      </main>

      <footer className="border-t border-stone-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 text-center text-xs text-stone-500">
          MealMatcher · Org.nr. 928 846 288 ·{' '}
          <a href="mailto:corporate@mealmatcher.no" className="hover:text-orange-600">
            corporate@mealmatcher.no
          </a>
        </div>
      </footer>
    </div>
  );
}
